import * as React from "react";
import { Award, Check, Copy, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { ApiError } from "@eduviaos/shared";
import { Button, Input, Label } from "@eduviaos/ui-kit";
import { QuestionCountPicker } from "../../components/sc/QuestionCountPicker";
import { clampQuestionCount } from "../../lib/learning/questionCount";
import { Reveal, SectionCard, SectionHeader } from "../../components/Section";
import { useLearnLocale } from "../../lib/i18n/useLearnLocale";
import {
  useLearningPreferences,
  useSkillCertificates,
  useStartSkillAssessment,
} from "../../lib/learning/hooks";
import type { Pathway, SkillAssessmentSession } from "../../lib/learning/types";
import { certificateVerifyPath } from "../../lib/learning/certificateVerify";
import { CertificateAttestationSummary } from "./CertificateAttestation";

export function SkillAssessmentPanel({
  pathway,
  onStarted,
}: {
  pathway: Pathway;
  onStarted: (session: SkillAssessmentSession) => void;
}) {
  const { strings } = useLearnLocale();
  const prefsQuery = useLearningPreferences();
  const prefs = prefsQuery.data;
  const maxQ = prefs?.max_questions_per_quiz ?? 15;
  const defaultQ = prefs?.default_skill_assessment_question_count ?? 5;

  const certsQuery = useSkillCertificates();
  const startMut = useStartSkillAssessment();
  const [subject, setSubject] = React.useState(pathway.subjects[0] ?? "");
  const [topic, setTopic] = React.useState("");
  const [questionCount, setQuestionCount] = React.useState(defaultQ);
  const [error, setError] = React.useState<string | null>(null);
  const [copiedCode, setCopiedCode] = React.useState<string | null>(null);
  const [copyError, setCopyError] = React.useState<string | null>(null);

  React.useEffect(() => {
    setQuestionCount(defaultQ);
  }, [defaultQ, pathway.id]);

  React.useEffect(() => {
    if (!copiedCode) return;
    const timer = window.setTimeout(() => setCopiedCode(null), 2000);
    return () => window.clearTimeout(timer);
  }, [copiedCode]);

  async function copyVerifyCode(code: string) {
    try {
      await navigator.clipboard.writeText(code);
      setCopyError(null);
      setCopiedCode(code);
    } catch {
      setCopyError(strings.verifyCodeCopyFailed);
    }
  }

  const pathwayCerts = React.useMemo(
    () =>
      (certsQuery.data?.items ?? [])
        .filter((c) => c.pathway_id === pathway.id)
        .sort((a, b) => b.issued_at.localeCompare(a.issued_at)),
    [certsQuery.data, pathway.id],
  );

  async function start() {
    setError(null);
    if (!subject.trim() || !topic.trim()) {
      setError(strings.skillSubjectTopicRequired);
      return;
    }
    try {
      const session = await startMut.mutateAsync({
        pathwayId: pathway.id,
        body: {
          subject: subject.trim(),
          topic: topic.trim(),
          question_count: clampQuestionCount(questionCount, 3, Math.min(15, maxQ)),
        },
      });
      onStarted(session);
    } catch (err) {
      // Backend messages are English-only; the two failures a learner can act
      // on get their own copy, everything else gets the localised fallback.
      if (err instanceof ApiError && err.status === 429) setError(strings.diagnosticError429);
      else if (err instanceof ApiError && err.status === 503) setError(strings.diagnosticError503);
      else if (err instanceof ApiError && err.status === 0) setError(strings.errorOffline);
      else setError(strings.skillAssessmentStartFailed);
    }
  }

  return (
    <div className="space-y-3">
      <Reveal>
        <SectionHeader title={strings.skillAssessment} />
      </Reveal>

      <Reveal index={1}>
        <SectionCard className="space-y-3">
          <p className="text-muted-foreground text-xs leading-relaxed">{strings.skillHint}</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor={`skill-subject-${pathway.id}`}>{strings.subject}</Label>
              <Input
                id={`skill-subject-${pathway.id}`}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={strings.skillSubjectPlaceholder}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={`skill-topic-${pathway.id}`}>{strings.skillTopic}</Label>
              <Input
                id={`skill-topic-${pathway.id}`}
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder={strings.skillTopicPlaceholder}
              />
            </div>
          </div>
          <QuestionCountPicker
            id={`skill-count-${pathway.id}`}
            label={strings.questions}
            value={questionCount}
            min={3}
            max={Math.min(15, maxQ)}
            onChange={setQuestionCount}
          />
          {error && (
            <p role="alert" className="text-destructive text-xs">
              {error}
            </p>
          )}
          <Button
            className="w-full sm:w-auto"
            disabled={startMut.isPending}
            onClick={() => void start()}
          >
            {startMut.isPending ? (
              <>
                <Loader2 className="mr-2 size-4 animate-spin" />
                {strings.buildingQuiz}
              </>
            ) : (
              strings.startAssessment
            )}
          </Button>
        </SectionCard>
      </Reveal>

      {pathwayCerts.length > 0 && (
        <Reveal index={2}>
          <SectionCard className="space-y-2">
            <p className="text-sm font-semibold">{strings.yourCertificates}</p>
            <ul className="divide-border/60 divide-y">
              {pathwayCerts.map((cert) => (
                <li key={cert.id} className="flex items-start gap-2 py-2 first:pt-0 last:pb-0">
                  <Award className="text-primary mt-0.5 size-4 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium">
                      {cert.subject} · {cert.topic}
                    </p>
                    <CertificateAttestationSummary cert={cert} />
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                      <Link
                        to={certificateVerifyPath(cert.verify_code, pathway.tenant_id)}
                        aria-label={strings.viewCertificateAria(cert.subject, cert.topic)}
                        className="text-primary hover:text-primary/80 focus-visible:ring-ring inline-flex items-center rounded text-[0.68rem] font-medium underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2"
                      >
                        {strings.viewCertificate}
                      </Link>
                      <button
                        type="button"
                        // The visible text flips to "Copied", but the label has
                        // to flip too or a screen reader never hears the change.
                        aria-label={
                          copiedCode === cert.verify_code
                            ? strings.verifyCodeCopied
                            : `${strings.copyVerifyCode} — ${cert.subject} · ${cert.topic}`
                        }
                        title={cert.verify_code}
                        onClick={() => void copyVerifyCode(cert.verify_code)}
                        className="text-muted-foreground hover:text-foreground focus-visible:ring-ring inline-flex items-center gap-1 rounded text-[0.68rem] transition-colors focus-visible:outline-none focus-visible:ring-2"
                      >
                        {copiedCode === cert.verify_code ? (
                          <Check className="size-3 shrink-0" aria-hidden />
                        ) : (
                          <Copy className="size-3 shrink-0" aria-hidden />
                        )}
                        <span className="break-all">
                          {copiedCode === cert.verify_code
                            ? strings.verifyCodeCopied
                            : `${cert.verify_code.slice(0, 12)}…`}
                        </span>
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
            {copyError && (
              <p role="alert" className="text-destructive text-xs">
                {copyError}
              </p>
            )}
          </SectionCard>
        </Reveal>
      )}

      {/* A failed fetch used to render as "no certificates at all", which
          reads as though the learner never earned one. */}
      {certsQuery.isError && (
        <Reveal index={2}>
          <SectionCard className="flex flex-wrap items-center justify-between gap-2">
            <p role="alert" className="text-muted-foreground text-xs">
              {strings.certificatesLoadFailed}
            </p>
            <Button variant="outline" size="sm" onClick={() => void certsQuery.refetch()}>
              {strings.retry}
            </Button>
          </SectionCard>
        </Reveal>
      )}
    </div>
  );
}
